import React, { Suspense, Component, ReactNode } from 'react';
import { LiveComponent, RemoteComponentProps } from '@amzn/aws-tc-xb-xps';
import { useLogger } from '@amzn/xb-hooks';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import Box from '@amzn/awsui-components-react/polaris/box';
import Container from '@amzn/awsui-components-react/polaris/container';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';

/** Emitted when we swallow a remote failure, so a dead tab still shows up in availability metrics. */
const LIVE_COMPONENT_FAIL_METRIC = 'LiveComponent-Fail';

interface ErrorBoundaryProps {
    children: ReactNode;
    onError: (error: Error, errorInfo: React.ErrorInfo) => void;
    /** Render nothing on failure, for a mount whose caller surfaces the error itself. */
    silent?: boolean;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Error boundary that renders an inline error message in place of the remote component, instead of
 * letting the error reach `PageErrorBoundary` in `TrackedPage`, which would replace the route with
 * the full-page "Something went wrong" AppLayout *and* emit `PageComponent-Fail` — an availability
 * datapoint consumed by XbAppMonitoringStack. A third-party XPS bundle failing to download should
 * not count against this app's page availability, so we stop it here and emit `LiveComponent-Fail`
 * instead.
 *
 * Note this boundary is a backstop, not the primary handler. `LiveComponent` already wraps the
 * remote subtree in `HostSideErrorBoundary`/`RemoteSideErrorBoundary`, and for a LiveComponent
 * (as opposed to a PageComponent) those degrade to a component-level fallback rather than E500Page.
 * What they cannot catch is the `React.lazy` rejection when the remote module itself fails to
 * load — a CDN 404, an empty bundle, or a network drop — because that is thrown above
 * `RemoteComponent`. That is the case this boundary exists for.
 *
 * No retry affordance: XPS memoizes the `React.lazy` component in a module-scope store and React
 * caches the rejection, so remounting this subtree re-throws the same error. Recovery needs a reload.
 *
 * Exported for testing purposes.
 */
export class TabErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.props.onError(error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.silent) {
                return null;
            }
            return (
                <Container>
                    <Box textAlign='center' padding='l'>
                        <StatusIndicator type='error' data-testid='live-component-error'>
                            Unable to load this content. Please try again later.
                        </StatusIndicator>
                    </Box>
                </Container>
            );
        }
        return this.props.children;
    }
}

/**
 * A fault-isolating wrapper for `LiveComponent` that:
 * 1. Provides the Suspense boundary that `React.lazy` requires, with a Spinner fallback shown while
 *    the remote XPS bundle downloads.
 * 2. Confines a failed remote load to this subtree — see {@link TabErrorBoundary}.
 *
 * Props are forwarded unchanged to `LiveComponent`.
 *
 * Deliberately does *not* pass `LiveComponent`'s own `fallback` prop. Despite being on
 * `RemoteComponentProps`, `fallback` is inert for `LiveComponent`: only `PageComponent` renders it
 * as a Suspense fallback (wrapped in `TimedSuspenseFallback`). `LiveComponent` forwards it to
 * `LazyRemoteComponentStore`, which excludes it from the cache key and hands it to the prefetch
 * interceptor, so passing a Spinner there renders nothing and just invites the reader to think the
 * outer `Suspense` below is redundant. It is not — it is what actually shows the Spinner.
 */
export const SuspenseLiveComponent: React.FC<
    RemoteComponentProps & {
        /**
         * Set for a mount that may legitimately render nothing. Suppresses both the Suspense
         * spinner and the error fallback, so a headless mount stays headless either way.
         */
        hideSuspenseFallback?: boolean;
        /**
         * Called when the remote subtree fails. Lets a caller that gates UI on this component
         * fail open, instead of hiding that UI forever because the remote never loaded.
         */
        onLoadError?: () => void;
    }
> = ({ hideSuspenseFallback = false, onLoadError, ...props }) => {
    const { count } = useLogger();

    return (
        <TabErrorBoundary
            silent={hideSuspenseFallback}
            onError={() => {
                count(LIVE_COMPONENT_FAIL_METRIC);
                onLoadError?.();
            }}
        >
            <Suspense fallback={hideSuspenseFallback ? null : <Spinner data-testid='live-component-spinner' />}>
                <LiveComponent {...props} />
            </Suspense>
        </TabErrorBoundary>
    );
};
